import { FilterModel } from '@ag-grid-community/core/dist/esm/es6/interfaces/iFilter';
import { IServerSideGetRowsParams } from '@ag-grid-community/core/dist/esm/es6/interfaces/iServerSideDatasource';
import { HttpClient } from '@angular/common/http';
import { Injectable, WritableSignal, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { environment } from '@env/environment';
import { Apollo, ApolloBase, gql } from 'apollo-angular';
import { ImportChannel } from 'exports/interfaces/imports-runner.interface';
import { Observable, map, switchMap } from 'rxjs';
import { DistributionRestrictionType } from 'settings/enums/distribution-restriction-type';
import { Brand } from 'settings/interfaces/brand.interface';
import { Channel } from 'settings/interfaces/channel.interface';
import { ChannelMappingTypeDetails } from 'settings/interfaces/mapping-type-details.interface';
import { ID_ZERO } from 'shared/constants/general-constants';
import { GridService } from 'shared/interfaces/grid-service';
import { CachingService } from 'shared/services';

const DISTRIBUTION_MANAGEMENT_API = environment.BASE_URL + 'distributionManagementApi';
const uploadURL = DISTRIBUTION_MANAGEMENT_API + '/upload';

@Injectable({
    providedIn: 'root',
})
export class ChannelsService implements GridService {
    private apollo: ApolloBase;

    public channelToEdit: WritableSignal<Channel | null> = signal(null);

    constructor(
        private apolloProvider: Apollo,
        private cachingService: CachingService,
        private http: HttpClient,
    ) {
        this.apollo = this.apolloProvider.use('distributionManagementAPI');
    }

    NAME = 'ChannelsGridService';

    loadImportsEligibleChannels = signal<boolean>(false);
    loadImportsEligibleChannels$ = toObservable(this.loadImportsEligibleChannels);

    importsEligibleChannels = toSignal(
        this.loadImportsEligibleChannels$.pipe(
            switchMap(() => {
                return this.http.get<ImportChannel[]>(DISTRIBUTION_MANAGEMENT_API + '/channels', { params: { isEligibleForImport: true } });
            }),
        ),
        { initialValue: [] as ImportChannel[] },
    );

    public getChannelsWithBrandCodeRequired(brand: Brand) {
        const channelsWithBrandCodeRequiredQuery = `
            query getChannelsWithBrandCodeRequired {
                channels {
                    searchBrandChannels(filtering: {
                        requiresBrandCodeForExport: true,
                        contentDistributionCategory: ${brand.contentDistributionCategory},
                        ${brand.contentDistributionRestrictionsInput ? 'contentChannelIds: [' + brand.contentDistributionRestrictionsInput.map((id) => `"${id}"`) + '],' : ''}
                        ${brand.visualsDistributionCategory ? 'visualsDistributionCategory:' + brand.visualsDistributionCategory + ',' : ''}
                        ${brand.visualsDistributionRestrictionsInput ? 'visualChannelIds: [' + brand.visualsDistributionRestrictionsInput.map((id) => `"${id}"`) + '],' : ''}
                    }) {
                        data {
                            id,
                            name,
                            channelMappings {
                                mappingType,
                                description
                            }
                        }
                    }
                }
            }
        `;

        return this.apollo
            .query({
                query: gql(channelsWithBrandCodeRequiredQuery),
            })
            .pipe(
                map((response: any) => {
                    return response.data.channels.searchBrandChannels.data;
                }),
            );
    }

    public createChannel(channel: Channel): Observable<any> {
        return this.apollo
            .mutate({
                mutation: gql`
                mutation createChannel {
                    channels {
                        createChannel (
                            createChannel: {
                                name: "${channel.name}",
                                isTestChannel: ${channel.isTestChannel},
                                isGdsChannel: ${channel.isGdsChannel},
                                isContentDistribution: ${channel.isContentDistribution},
                                isVisualsDistribution: ${channel.isVisualsDistribution},
                                requiresMappedIdForExport: ${channel.requiresMappedIdForExport},
                                requiresBrandCodeForExport: ${channel.requiresBrandCodeForExport},
                                acceptsMappingsForRoomTypes: ${channel.acceptsMappingsForRoomTypes},
                                acceptsMappingsForRatePlans: ${channel.acceptsMappingsForRatePlans},
                                guaranteeMappingRequired: ${channel.guaranteeMappingRequired},
                                channelLanguagesInput: [${channel.languageIds.map((id) => `{languageId: "${id}"}`)}],
                                channelFeatureGroupsInput: [${channel.featureGroupIds.map((id) => `{featureGroupId: "${id}"}`)}],
                                ${channel.sharedIdentifier ? 'sharedIdentifier: "' + channel.sharedIdentifier + '"' : ''}
                            }) {
                            data {
                                name,
                                id
                            }
                        }
                    }
                }
            `,
            })
            .pipe(
                map((response: any) => {
                    return response.data.channels.createChannel.data[0];
                }),
            );
    }

    public getRows(params: IServerSideGetRowsParams, pageNumber: number, pageSize: number): Observable<any> {
        const filterModel = params.request.filterModel as FilterModel;
        const acceptsMappingsForRatePlans = filterModel['acceptsMappingsForRatePlans']?.filter;
        const acceptsMappingsForRoomTypes = filterModel['acceptsMappingsForRoomTypes']?.filter;
        const requiresBrandCodeForExport = filterModel['requiresBrandCodeForExport']?.filter;
        const requiresMappedIdForExport = filterModel['requiresMappedIdForExport']?.filter;
        const guaranteeMappingRequired = filterModel['guaranteeMappingRequired']?.filter;
        const isContentDistribution = filterModel['isContentDistribution']?.filter;
        const isVisualsDistribution = filterModel['isVisualsDistribution']?.filter;

        return this.apollo
            .query({
                query: gql`
                query getChannels (
                    $orderBy: String,
                    $orderDirection: OrderDirection,
                    $channelName: String,
                    $requiresMappedIdForExport: Boolean,
                    $requiresBrandCodeForExport: Boolean,
                    $acceptsMappingsForRoomTypes: Boolean,
                    $acceptsMappingsForRatePlans: Boolean,
                    $guaranteeMappingRequired: Boolean,
                    $isContentDistribution: Boolean,
                    $isVisualsDistribution: Boolean,
                ) {
                    channels {
                        searchChannels(
                            searchQuery:
                            {
                                pageNumber: ${pageNumber},
                                pageSize: ${pageSize},
                                orderBy: $orderBy,
                                orderDirection: $orderDirection
                            },
                            filtering:
                            {
                                name: $channelName,
                                requiresMappedIdForExport: $requiresMappedIdForExport,
                                requiresBrandCodeForExport: $requiresBrandCodeForExport,
                                acceptsMappingsForRoomTypes: $acceptsMappingsForRoomTypes,
                                acceptsMappingsForRatePlans: $acceptsMappingsForRatePlans,
                                guaranteeMappingRequired: $guaranteeMappingRequired,
                                isContentDistribution: $isContentDistribution,
                                isVisualsDistribution: $isVisualsDistribution
                            })
                        {
                            data {
                                id,
                                name,
                                isGdsChannel,
                                isTestChannel,
                                isContentDistribution,
                                isVisualsDistribution,
                                requiresMappedIdForExport,
                                requiresBrandCodeForExport,
                                acceptsMappingsForRoomTypes,
                                acceptsMappingsForRatePlans,
                                guaranteeMappingRequired,
                            }
                            totalCount
                        }
                    }
                }
            `,
                variables: {
                    orderBy: params.request.sortModel[0]?.colId ?? 'name',
                    orderDirection: params.request.sortModel[0]?.sort.toUpperCase() ?? 'ASC',
                    channelName: filterModel['name']?.filter,
                    acceptsMappingsForRatePlans: acceptsMappingsForRatePlans ? JSON.parse(acceptsMappingsForRatePlans) : null,
                    acceptsMappingsForRoomTypes: acceptsMappingsForRoomTypes ? JSON.parse(acceptsMappingsForRoomTypes) : null,
                    requiresBrandCodeForExport: requiresBrandCodeForExport ? JSON.parse(requiresBrandCodeForExport) : null,
                    requiresMappedIdForExport: requiresMappedIdForExport ? JSON.parse(requiresMappedIdForExport) : null,
                    guaranteeMappingRequired: guaranteeMappingRequired ? JSON.parse(guaranteeMappingRequired) : null,
                    isContentDistribution: isContentDistribution ? JSON.parse(isContentDistribution) : null,
                    isVisualsDistribution: isVisualsDistribution ? JSON.parse(isVisualsDistribution) : null,
                },
                fetchPolicy: 'no-cache',
            })
            .pipe(
                map((response: any) => {
                    return response.data.channels.searchChannels;
                }),
            );
    }

    public getAllChannels(distributionRestriction?: DistributionRestrictionType): Observable<Channel[]> {
        const isContentDistribution = distributionRestriction === DistributionRestrictionType.CONTENT;
        const isVisualsDistribution = distributionRestriction === DistributionRestrictionType.VISUALS;

        return this.apollo
            .query({
                query: gql`
                query get${distributionRestriction ? (distributionRestriction === DistributionRestrictionType.CONTENT ? 'Content' : 'Visuals') : 'All'}Channels {
                    channels {
                        searchChannels(searchQuery: {
                            pageNumber: 1,
                            pageSize: 1000
                        }
                        ${
                            distributionRestriction
                                ? `filtering: {
                            ${isContentDistribution ? 'isContentDistribution: true' : ''},
                            ${isVisualsDistribution ? 'isVisualsDistribution: true' : ''}
                            }`
                                : ''
                        }
                        ) {
                            data {
                                id,
                                name,
                                isTestChannel,
                                isGdsChannel,
                                isContentDistribution,
                                isVisualsDistribution
                            }
                            totalCount
                        }
                    }
                }
            `,
                fetchPolicy: 'no-cache',
            })
            .pipe(
                map((response: any) => {
                    return response.data.channels.searchChannels.data;
                }),
            );
    }

    public getChannelById(channelId: string): Observable<any> {
        return this.apollo
            .query({
                query: gql`
                query getChannelById {
                    channels {
                        searchChannels(filtering: {searchTerm: "${channelId}"}) {
                            data {
                                id,
                                name,
                                logo,
                                isTestChannel,
                                isGdsChannel,
                                isContentDistribution,
                                isVisualsDistribution,
                                requiresMappedIdForExport,
                                requiresBrandCodeForExport,
                                acceptsMappingsForRoomTypes,
                                acceptsMappingsForRatePlans,
                                guaranteeMappingRequired
                                channelLanguages {
                                    languageId
                                },
                                channelFeatureGroups {
                                    featureGroupId
                                },
                                sharedIdentifier,
                                entityTracking {
                                    createdBy,
                                    createdAt,
                                    modifiedBy,
                                    modifiedAt
                                }
                            }
                        }
                    }
                }
            `,
                fetchPolicy: 'no-cache',
            })
            .pipe(
                map((response: any) => {
                    return response.data.channels.searchChannels.data[0];
                }),
            );
    }

    public updateChannel(channel: Channel): Observable<any> {
        const channelMutation = `
                mutation updateChannel {
                    channels {
                        updateChannel (updateChannel: {
                                id: "${channel.id}",
                                ${channel.name ? 'name: "' + channel.name + '"' : ''},
                                ${channel.isTestChannel !== undefined ? 'isTestChannel:' + channel.isTestChannel : ''},
                                ${channel.isGdsChannel !== undefined ? 'isGdsChannel:' + channel.isGdsChannel : ''},
                                ${channel.isContentDistribution !== undefined ? 'isContentDistribution:' + channel.isContentDistribution : ''},
                                ${channel.isVisualsDistribution !== undefined ? 'isVisualsDistribution:' + channel.isVisualsDistribution : ''},
                                ${channel.requiresMappedIdForExport !== undefined ? 'requiresMappedIdForExport:' + channel.requiresMappedIdForExport : ''},
                                ${channel.requiresBrandCodeForExport !== undefined ? 'requiresBrandCodeForExport:' + channel.requiresBrandCodeForExport : ''},
                                ${channel.acceptsMappingsForRoomTypes !== undefined ? 'acceptsMappingsForRoomTypes:' + channel.acceptsMappingsForRoomTypes : ''},
                                ${channel.acceptsMappingsForRatePlans !== undefined ? 'acceptsMappingsForRatePlans:' + channel.acceptsMappingsForRatePlans : ''},
                                ${channel.guaranteeMappingRequired !== undefined ? 'guaranteeMappingRequired:' + channel.guaranteeMappingRequired : ''},
                                ${channel.languageIds ? 'channelLanguagesInput: [' + channel.languageIds.map((id) => `{languageId: "${id}"}`) + ']' : ''},
                                ${channel.featureGroupIds ? 'channelFeatureGroupsInput: [' + channel.featureGroupIds.map((id) => `{featureGroupId:"${id}"}`) + ']' : ''},
                                ${channel.logo === null ? 'logo: null' : ''},
                                ${channel.sharedIdentifier ? 'sharedIdentifier: "' + channel.sharedIdentifier + '"' : ''}
                            }) {
                            statusCode,
                            message
                        }
                    }
                }
        `;

        return this.apollo
            .mutate({
                mutation: gql(channelMutation),
            })
            .pipe(
                map((response: any) => {
                    return response;
                }),
            );
    }

    public deleteChannel(channelId: string) {
        const deleteMutation = `
                mutation removeChannel {
                    channels {
                        removeChannel (removeChannel: {id: "${channelId}"}) {
                            statusCode,
                            message
                        }
                    }
                }
        `;

        return this.apollo
            .mutate({
                mutation: gql(deleteMutation),
            })
            .pipe(
                map((response: any) => {
                    return response;
                }),
            );
    }

    public getChannelsWithMappedIdRequired(): Observable<Channel[]> {
        return this.apollo
            .query({
                query: gql`
                    query getChannelsWithMappedIdRequired {
                        channels {
                            searchChannels(searchQuery: { pageNumber: 1, pageSize: 1000 }, filtering: { requiresMappedIdForExport: true }) {
                                data {
                                    id
                                    name
                                    isTestChannel
                                    isGdsChannel
                                }
                                totalCount
                            }
                        }
                    }
                `,
                fetchPolicy: 'no-cache',
            })
            .pipe(
                map((response: any) => {
                    return response.data.channels.searchChannels.data;
                }),
            );
    }

    public getAllChannelMappings(channelId: string): Observable<any> {
        return this.apollo
            .query({
                query: gql`
                query search {
                    channels {
                        searchChannelMappings(filtering: {channelId: "${channelId}"}) {
                            data {
                                id,
                                description,
                                regexValidation,
                                isUnique,
                                isDefault,
                                channelId,
                            }
                        }
                    }
                }
            `,
                fetchPolicy: 'no-cache',
            })
            .pipe(
                map((response: any) => {
                    return response.data.channels.searchChannelMappings.data;
                }),
            );
    }

    public getChannelsForFullExport(includeCustomChannels: boolean = false): Observable<any> {
        if (includeCustomChannels) {
            return this.http.get(DISTRIBUTION_MANAGEMENT_API + '/channels', { params: { includeCustomChannelsForExport: true } });
        } else {
            return this.http.get(DISTRIBUTION_MANAGEMENT_API + '/channels', { params: { includeChannelsForExport: true } });
        }
    }

    public updateMultipleChannelMappingTypes(mappingTypes: ChannelMappingTypeDetails[]): Observable<any> {
        const mutation = `
                mutation updateMultipleChannelMappings {
                    channels {
                        updateMultipleChannelMappings (updateMultipleChannelMappings: [ ${mappingTypes?.map(
                            (mappingType: ChannelMappingTypeDetails) => `{
                            channelId: "${mappingType.channelId}",
                            description: "${mappingType.description}",
                            id: "${mappingType.id !== String.EMPTY ? mappingType.id : ID_ZERO}",
                            isDefault: ${mappingType.isDefault},
                            isUnique: ${mappingType.isUnique},
                            regexValidation: "${mappingType.regexValidation}"
                        },`,
                        )} ]) {
                            statusCode,
                            message
                        }
                    }
                }
        `;
        return this.apollo
            .mutate({
                mutation: gql(mutation),
            })
            .pipe(
                map((response: any) => {
                    return response;
                }),
            );
    }

    public uploadFile(fileToUpload: File, channelId: string): Observable<any> {
        const formData = new FormData();
        formData.append('file', fileToUpload);
        formData.append('channelId', channelId);

        return this.http.post(uploadURL, formData, { responseType: 'text' });
    }
}
